/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/43fgc52/screen/54135086
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import find from 'ramda/src/find';
import filter from 'ramda/src/filter';
import propEq from 'ramda/src/propEq';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import * as HabitCard from '@components/LibraryToDoHabit';
import * as HabitScheduleCard from '@components/LibraryToDoHabitSchedule';
import * as QuotaCard from '@components/LibraryToDoQuota';
import * as Scorecard from '@components/LibraryToDoScoreCard';
import { ScorecardDetailPdf } from '@components/LibraryToDoScoreCard/PdfIcon';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';
import { ToDoTypes } from '~/services/config';
import withNavigate from '~/hocs/withNavigate';
import { ScoreCardTypes } from '../../../../services/config';

class ToDoDetail extends React.Component {
    constructor(props) {
        super(props);
        const type = props.match?.params?.activeTab;
        const id = Number(props.match?.params?.id || 0);

        this.state = { id, type };

        if (type === 'scorecards' && props.templates?.[type]?.data) {
            const detail = find(propEq('id', id), props.templates[type].data);
            if (detail?.data?.quotas) {
                const templateIds = detail.data.quotas.map((quota) => quota.quota_template_id);
                props.getQuotas(id, type, templateIds);
            }
        }
    }

    handleSelectMenu = (e, quotas, detail) => {
        const { type, id } = this.state;
        const { templates } = this.props;
        const disabled = filter((x) => x !== type, ToDoTypes);
        const card = find(propEq('id', id), templates?.[type]?.data || []);

        const active = ScoreCardTypes[card?.type]?.value.toLowerCase() || 'monthly';
        switch (e) {
            case 'edit':
                if (type === 'scorecards' || type === 'quotas') {
                    this.props.navigate(`/library/${type}/${id}/edit`);
                } else {
                    this.props.navigate(`/library/to-do/${type}/${id}/edit`);
                }
                break;
            case 'delete':
                this.props.toggleModal({
                    type: 'Confirm',
                    data: {
                        before: {
                            title: 'Delete',
                            body: 'Are you sure you want to delete this item?',
                        },
                    },
                    callBack: {
                        onYes: () => this.handleDeleteTemplate(id),
                    },
                });
                break;
            case 'assign':
                if (type === 'scorecards') {
                    this.props.toggleModal({
                        type: 'Assign ScoreCard',
                        data: { before: { modules: [card], disabled, active: active }, after: [] },
                    });
                } else {
                    this.props.toggleModal({
                        type: 'Assign ToDo',
                        data: { before: { modules: [card], disabled }, after: [] },
                    });
                }
                break;
            case 'print':
                ScorecardDetailPdf(detail, quotas);
                break;
            case 'duplicate':
                this.props.toggleModal({
                    type: 'Duplicate Module',
                    data: { before: { type: 'scorecards', object: card } },
                });
                break;
            default:
                break;
        }
    };

    handleDeleteTemplate = (id) => {
        const { type } = this.state;
        let route = null;
        let target = 'template';
        if (type === 'tracks') {
            target = 'track';
        } else if (type === 'quotas') {
            route = '/library/quotas';
            target = 'quota_template';
        } else if (type === 'scorecards') {
            target = 'scorecard';
        }

        this.props.deleteTemplate(target, 'delete', id, null, route, {
            type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
            target: type,
            payload: { page: 1, perPage: 25 },
        });
    };

    render() {
        const { templates, userId, currentCompanyInfo, authors, departments, categories, competencies, role } = this.props;
        const { id, type } = this.state;

        // Add safety check for templates loading
        if (!templates || !type) {
            return (
                <ErrorBoundary>
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div>Loading library details...</div>
                    </div>
                </ErrorBoundary>
            );
        }

        const card = this.props.location?.state?.card;
        const detail = find(propEq('id', id), templates?.[type]?.data || []) || card;

        return (
            <ErrorBoundary>
                {type === 'habits' && (
                    <HabitCard.Detail
                        userRole={role}
                        authors={authors}
                        departments={departments}
                        competencies={competencies}
                        categories={categories}
                        data={detail}
                        navigate={this.props.navigate}
                        location={this.props.location}
                        userId={userId}
                        companyId={currentCompanyInfo.id}
                        onSave={(e) => this.props.saveTemplate(e)}
                        onSelect={this.handleSelectMenu}
                    />
                )}
                {type === 'habitslist' && (
                    <HabitScheduleCard.Detail
                        userRole={role}
                        data={detail}
                        authors={authors}
                        departments={departments}
                        competencies={competencies}
                        categories={categories}
                        navigate={this.props.navigate}
                        location={this.props.location}
                        userId={userId}
                        companyId={currentCompanyInfo.id}
                        onSelect={this.handleSelectMenu}
                    />
                )}
                {type === 'quotas' && (
                    <QuotaCard.Detail
                        userRole={role}
                        authors={authors}
                        departments={departments}
                        competencies={competencies}
                        categories={categories}
                        data={detail}
                        navigate={this.props.navigate}
                        location={this.props.location}
                        userId={userId}
                        companyId={currentCompanyInfo.id}
                        onSelect={this.handleSelectMenu}
                    />
                )}
                {type === 'scorecards' && (
                    <Scorecard.Detail
                        userRole={role}
                        authors={authors}
                        departments={departments}
                        competencies={competencies}
                        categories={categories}
                        data={detail}
                        navigate={this.props.navigate}
                        location={this.props.location}
                        userId={userId}
                        onSelect={this.handleSelectMenu}
                        companyId={currentCompanyInfo.id}
                    />
                )}
            </ErrorBoundary>
        );
    }
}

ToDoDetail.propTypes = {
    role: PropTypes.number,
    templates: PropTypes.any,
    companyId: PropTypes.number,
    authors: PropTypes.array,
    departments: PropTypes.array,
    competencies: PropTypes.array,
    categories: PropTypes.array,
    getQuotas: PropTypes.func,
    toggleModal: PropTypes.func,
    saveTemplate: PropTypes.func,
    deleteTemplate: PropTypes.func,
};

ToDoDetail.defaultProps = {
    role: 1,
    companyId: 0,
    authors: [],
    departments: [],
    competencies: [],
    categories: [],
    scorecardsTags: [],
    templates: {},
    getQuotas: () => {},
    saveTemplate: () => {},
    toggleModal: () => {},
    deleteTemplate: () => {},
};

const mapStateToProps = (state) => ({
    role: state.app.app_role_id,
    userId: state.app.id,
    currentCompanyInfo: state.app.company_info,
    templates: state.develop.templates,
    companyId: state.app.company_info.id,
    authors: state.app.authors,
    departments: state.app.departments,
    competencies: state.app.competencies,
    categories: state.app.categories,
});

const mapDispatchToProps = (dispatch) => ({
    getQuotas: (id, type, templateIds) => dispatch(DevelopActions.quotatemplatesRequest(id, type, templateIds)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
    saveTemplate: (e) => dispatch(DevelopActions.librarysaveRequest(e)),
    deleteTemplate: (target, event, id, data, route, after) => dispatch(DevelopActions.libraryeventRequest(target, event, id, data, route, after)),
    onDuplicate: (e) => dispatch(DevelopActions.librarysaveRequest(e)),
});

const ToDoDetailWithNavigate = withNavigate(ToDoDetail);

export default connect(mapStateToProps, mapDispatchToProps)(ToDoDetailWithNavigate);
